/**
 * "Repetir em horários" (docs/refactor/66): how a loop repeats, as people say it, and the five-field cron the server
 * keeps. The picker edits a {@link ScheduleDraft}; {@link scheduleRequest} turns it into what the API takes, and
 * {@link describeSchedule} turns a saved loop back into a sentence ("Dias úteis às 09:00").
 */
import { formatInterval } from './workInterval'

export type ScheduleMode = 'interval' | 'daily' | 'weekdays' | 'days' | 'monthly' | 'custom'

export interface ScheduleDraft {
  mode: ScheduleMode
  /** For "A cada": minutes between runs. */
  minutes: number
  /** "HH:MM", for the clock modes. */
  time: string
  /** Days of the week, 0 = Sunday … 6 = Saturday, for "Em dias da semana". */
  days: number[]
  /** Day of the month, for "Todo mês". */
  dayOfMonth: number
  /** The expression itself, for "Avançado". */
  cron: string
}

export const INTERVALS = [15, 30, 60, 180, 360, 720, 1440]

export const DEFAULT_SCHEDULE: ScheduleDraft = { mode: 'weekdays', minutes: 60, time: '09:00', days: [1, 3, 5], dayOfMonth: 1, cron: '0 9 * * 1-5' }

type T = (key: string, vars?: Record<string, string | number>) => string

/** The zone the person's clock is in, as the server reads cron: IANA, from the browser. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function clock(time: string): [number, number] {
  const [h = Number.NaN, m = Number.NaN] = time.split(':').map((part) => Number.parseInt(part, 10))
  return [Number.isFinite(h) ? Math.min(Math.max(h, 0), 23) : 9, Number.isFinite(m) ? Math.min(Math.max(m, 0), 59) : 0]
}

/** The cron for a clock mode; null for "A cada". */
export function cronOf(draft: ScheduleDraft): string | null {
  const [h, m] = clock(draft.time)
  switch (draft.mode) {
    case 'interval':
      return null
    case 'daily':
      return `${m} ${h} * * *`
    case 'weekdays':
      return `${m} ${h} * * 1-5`
    case 'days':
      return `${m} ${h} * * ${[...new Set(draft.days)].sort((a, b) => a - b).join(',') || '1'}`
    case 'monthly':
      return `${m} ${h} ${Math.min(Math.max(draft.dayOfMonth, 1), 28)} * *`
    case 'custom':
      return draft.cron.trim().replace(/\s+/g, ' ')
  }
}

/** What the API takes: an interval, or a cron and the zone it is read in — never both. */
export function scheduleRequest(draft: ScheduleDraft, timeZone = browserTimeZone()) {
  const cron = cronOf(draft)
  return cron ? { intervalMinutes: null, cron, timeZone } : { intervalMinutes: draft.minutes, cron: null, timeZone: null }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A saved loop back into the picker, for "Editar". Anything the presets cannot say stays in "Avançado". */
export function draftOf(order: { intervalMinutes: number | null; cron?: string | null }): ScheduleDraft {
  if (!order.cron) return { ...DEFAULT_SCHEDULE, mode: 'interval', minutes: order.intervalMinutes ?? 60 }
  const parts = order.cron.trim().split(/\s+/)
  const [minute = '', hour = '', dom = '', month = '', dow = ''] = parts
  const base = { ...DEFAULT_SCHEDULE, cron: order.cron }
  if (parts.length !== 5 || !/^\d+$/.test(minute) || !/^\d+$/.test(hour) || month !== '*') return { ...base, mode: 'custom' }
  const time = `${pad(Number(hour))}:${pad(Number(minute))}`
  if (dom === '*' && dow === '*') return { ...base, mode: 'daily', time }
  if (dom === '*' && dow === '1-5') return { ...base, mode: 'weekdays', time }
  if (dom === '*' && /^[0-6](,[0-6])*$/.test(dow)) return { ...base, mode: 'days', time, days: dow.split(',').map(Number) }
  if (dow === '*' && /^\d+$/.test(dom) && Number(dom) >= 1 && Number(dom) <= 28) return { ...base, mode: 'monthly', time, dayOfMonth: Number(dom) }
  return { ...base, mode: 'custom' }
}

/** "seg", "ter"… in the person's language: 1 November 2026 was a Sunday. */
export function weekdayName(locale: string, day: number): string {
  return new Date(2026, 10, 1 + day).toLocaleDateString(locale, { weekday: 'short' }).replace(/\.$/, '')
}

function list(locale: string, items: string[]): string {
  try {
    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(items)
  } catch {
    return items.join(', ')
  }
}

/** A loop's repetition as a sentence: "Todo dia às 09:00", "Seg, qua e sex às 08:00", "A cada 1 hora". */
export function describeSchedule(t: T, locale: string, order: { intervalMinutes: number | null; cron?: string | null }): string {
  if (!order.cron) return order.intervalMinutes ? t('work.everyShort', { interval: formatInterval(t, order.intervalMinutes) }) : ''
  const draft = draftOf(order)
  const time = draft.time
  switch (draft.mode) {
    case 'daily':
      return t('schedule.say.daily', { time })
    case 'weekdays':
      return t('schedule.say.weekdays', { time })
    case 'days': {
      const names = draft.days.map((day) => weekdayName(locale, day))
      const said = list(locale, names)
      return t('schedule.say.days', { days: said.charAt(0).toUpperCase() + said.slice(1), time })
    }
    case 'monthly':
      return t('schedule.say.monthly', { day: draft.dayOfMonth, time })
    default:
      return t('schedule.say.custom', { cron: order.cron })
  }
}
